import React, { useState } from 'react'
import { Button, Card, Empty, Flex, List, Popconfirm, Typography } from 'antd'
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../auth'
import { UPDATE_SITE } from '../../user/permission'
import { TimeAgo } from '../../components/time-ago'
import { useAnswers, useDeleteAnswer } from '../hooks'
import { SiteAnswer } from '../site'
import { AnswerModal } from './answer-modal'

/**
 * Answers the company added for questions Evaa could not answer from the website.
 */
export function SiteAnswers({ siteId }: { siteId: string }) {
    const { t } = useTranslation()
    const { can } = useAuth()
    const { data, isLoading } = useAnswers(siteId)
    const remove = useDeleteAnswer(siteId)
    const [editing, setEditing] = useState<SiteAnswer | 'new' | null>(null)
    const canEdit = can(UPDATE_SITE)

    return (
        <Card
            title={t('Added answers')}
            extra={
                canEdit && (
                    <Button
                        icon={<PlusOutlined />}
                        onClick={() => setEditing('new')}
                    >
                        {t('Add answer')}
                    </Button>
                )
            }
        >
            <Typography.Paragraph type="secondary">
                {t(
                    'Answers to questions that are not on the website. Evaa uses them next to the pages of the website.'
                )}
            </Typography.Paragraph>
            <List
                loading={isLoading}
                dataSource={data ?? []}
                pagination={{ pageSize: 5, hideOnSinglePage: true, size: 'small' }}
                locale={{
                    emptyText: (
                        <Empty
                            description={t(
                                'No answers yet. Add one from a question Evaa could not answer, at the top of the chats.'
                            )}
                        />
                    ),
                }}
                renderItem={(answer) => (
                    <List.Item
                        actions={
                            canEdit
                                ? [
                                      <Button
                                          key="edit"
                                          type="text"
                                          icon={<EditOutlined />}
                                          aria-label={t('Change answer')}
                                          onClick={() => setEditing(answer)}
                                      />,
                                      <Popconfirm
                                          key="delete"
                                          title={t('Remove this answer?')}
                                          okText={t('Remove')}
                                          cancelText={t('Cancel')}
                                          onConfirm={() => remove.mutate(answer.id)}
                                      >
                                          <Button
                                              type="text"
                                              danger
                                              icon={<DeleteOutlined />}
                                              aria-label={t('Remove')}
                                          />
                                      </Popconfirm>,
                                  ]
                                : []
                        }
                    >
                        <Flex
                            vertical
                            gap={2}
                            className="min-w-0"
                        >
                            <Typography.Text strong>{answer.question}</Typography.Text>
                            <Typography.Text className="whitespace-pre-wrap">{answer.answer}</Typography.Text>
                            <Typography.Text
                                type="secondary"
                                className="text-xs"
                            >
                                {answer.createdBy && `${answer.createdBy} · `}
                                <TimeAgo date={answer.updatedAt} />
                            </Typography.Text>
                        </Flex>
                    </List.Item>
                )}
            />
            <AnswerModal
                siteId={siteId}
                answer={editing !== null && editing !== 'new' ? editing : undefined}
                open={editing !== null}
                onClose={() => setEditing(null)}
            />
        </Card>
    )
}
